/**
 * Phase F1 — Portfolio Rules Editor
 * ---------------------------------
 * Exposes Phase 4 portfolio-rules / funding-rules / distribution-rules APIs
 * plus the existing /deploy-check readiness endpoint.
 *
 * Endpoints used:
 *   GET    /api/portfolios/{id}                  — basic portfolio metadata
 *   GET    /api/portfolios/{id}/rules            — portfolio_rules
 *   PUT    /api/portfolios/{id}/rules            — upsert portfolio_rules
 *   GET    /api/portfolios/{id}/funding-rules    — funding_rules
 *   POST   /api/portfolios/{id}/funding-rules    — upsert funding_rules
 *   GET    /api/distribution-rules/{id}          — distribution_rules
 *   POST   /api/portfolios/{id}/distribution-rules — upsert distribution_rules
 *   POST   /api/portfolios/{id}/deploy-check     — deployment readiness
 *
 * No backend logic added. Pure UI.
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Settings, Coins, Users, Target, Boxes, Rocket, CheckCircle2, XCircle, Save,
  ArrowLeft, AlertCircle,
} from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PORTFOLIO_TYPES = [
  { value: 'UTILITY_ONLY',     label: 'Utility Only',
    description: 'No distributions, no provider required. Pure utility NFTs.' },
  { value: 'PROVIDER_LINKED',  label: 'Provider Linked',
    description: 'Distributions via 4-pool model. Requires at least one provider.' },
  { value: 'MARKET_LINKED',    label: 'Market Linked',
    description: 'Market participation. Performance-driven distributions.' },
];
const FUNDING_SOURCES = [
  { value: 'CREATOR_FUNDED', label: 'Creator Funded' },
  { value: 'SALES_FUNDED',   label: 'Sales Funded' },
  { value: 'HYBRID',         label: 'Hybrid Funded' },
];
const DIST_METHODS = [
  { value: 'EQUAL',    label: 'Equal Distribution' },
  { value: 'WEIGHTED', label: 'Weighted Distribution' },
];
const SELLOUT_POLICIES = [
  { value: 'UNLOCK_AT_SELLOUT',   label: 'Unlock At Sellout' },
  { value: 'UNLOCK_AT_THRESHOLD', label: 'Unlock At Threshold' },
  { value: 'CUSTOM',              label: 'Custom' },
];
const NFT_ASSIGNMENT = [
  { value: 'RANDOM',           label: 'Random' },
  { value: 'SEQUENTIAL',       label: 'Sequential' },
  { value: 'CREATOR_SELECTED', label: 'Creator Selected' },
];
const SUPPLY_POLICIES = [
  { value: 'FIXED',       label: 'Fixed Supply' },
  { value: 'EXPANDABLE',  label: 'Expandable Supply' },
];

const inputStyle = {
  width: '100%', padding: '11px 14px', background: theme.colors.bg.page,
  border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.sm,
  color: theme.colors.text.primary, fontSize: '14px', outline: 'none', boxSizing: 'border-box',
};
const labelStyle = {
  display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600,
  color: theme.colors.text.secondary,
};
const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, padding: 24,
  boxShadow: theme.colors.shadows.sm,
};
const sectionTitle = {
  fontSize: 16, fontWeight: 700, color: theme.colors.text.primary, marginBottom: 14,
  display: 'flex', alignItems: 'center', gap: 8,
};

const PortfolioRulesEditor = () => {
  const { portfolioId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({ rules: false, funding: false, distribution: false });
  const [portfolio, setPortfolio] = useState(null);
  const [providerCount, setProviderCount] = useState(0);
  const [deployCheck, setDeployCheck] = useState(null);

  // Rules form state
  const [rules, setRules] = useState({
    portfolio_type: 'UTILITY_ONLY',
    sellout_unlock_policy: 'UNLOCK_AT_SELLOUT',
    nft_assignment_method: 'SEQUENTIAL',
    supply_policy: 'FIXED',
    supply_max_cap: null,
    market_participation_enabled: false,
  });
  const [funding, setFunding] = useState({
    funding_type: 'USD',
    funding_source: 'SALES_FUNDED',
    threshold_amount: 0,
    target_amount: 0,
    provider_service_fee_percent: 0,
    provider_service_fee_flat: 0,
    refund_on_failure: true,
  });
  const [distribution, setDistribution] = useState({
    method: 'EQUAL',
    creator_percent: 20,
    buyer_percent: 50,
    provider_percent: 20,
    platform_fee_percent: 10,
  });

  const loadAll = async () => {
    setLoading(true);
    try {
      // Portfolio metadata
      const pRes = await axios.get(`${API}/portfolios/${portfolioId}`);
      setPortfolio(pRes.data.portfolio || pRes.data);

      // Existing rules (may not exist yet)
      const [rRes, fRes, dRes, paRes, dcRes] = await Promise.allSettled([
        axios.get(`${API}/portfolios/${portfolioId}/rules`),
        axios.get(`${API}/portfolios/${portfolioId}/funding-rules`),
        axios.get(`${API}/distribution-rules/${portfolioId}`),
        axios.get(`${API}/portfolios/${portfolioId}/provider-assignments`),
        axios.post(`${API}/portfolios/${portfolioId}/deploy-check`),
      ]);
      if (rRes.status === 'fulfilled' && rRes.value.data.rules) {
        const r = rRes.value.data.rules;
        setRules({
          portfolio_type:               r.portfolio_type || 'UTILITY_ONLY',
          sellout_unlock_policy:        r.sellout_unlock_policy || 'UNLOCK_AT_SELLOUT',
          nft_assignment_method:        r.nft_assignment_method || 'SEQUENTIAL',
          supply_policy:                r.supply_policy || 'FIXED',
          supply_max_cap:               r.supply_max_cap ?? null,
          market_participation_enabled: !!r.market_participation_enabled,
        });
      }
      if (fRes.status === 'fulfilled' && fRes.value.data.funding_rules) {
        const f = fRes.value.data.funding_rules;
        setFunding({
          funding_type:                f.funding_type || 'USD',
          funding_source:              f.funding_source || 'SALES_FUNDED',
          threshold_amount:            f.threshold_amount ?? 0,
          target_amount:               f.target_amount ?? 0,
          provider_service_fee_percent: f.provider_service_fee_percent ?? 0,
          provider_service_fee_flat:    f.provider_service_fee_flat ?? 0,
          refund_on_failure:            f.refund_on_failure ?? true,
        });
      }
      if (dRes.status === 'fulfilled' && dRes.value.data.distribution_rules) {
        const d = dRes.value.data.distribution_rules;
        setDistribution({
          method:               d.method || 'EQUAL',
          creator_percent:      d.creator_percent ?? 20,
          buyer_percent:        d.buyer_percent ?? 50,
          provider_percent:     d.provider_percent ?? 20,
          platform_fee_percent: d.platform_fee_percent ?? 10,
        });
      }
      if (paRes.status === 'fulfilled') {
        const list = paRes.value.data.assignments || paRes.value.data.provider_assignments || [];
        const active = list.filter((a) => ['ACCEPTED', 'ACTIVE'].includes(a.status));
        setProviderCount(active.length);
      }
      if (dcRes.status === 'fulfilled') {
        setDeployCheck(dcRes.value.data);
      }
    } catch (err) {
      toast.error('Failed to load portfolio rules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [portfolioId]);

  const refreshDeployCheck = async () => {
    try {
      const r = await axios.post(`${API}/portfolios/${portfolioId}/deploy-check`);
      setDeployCheck(r.data);
    } catch (e) { /* ignore */ }
  };

  const saveRules = async () => {
    setSaving((s) => ({ ...s, rules: true }));
    try {
      const payload = { ...rules };
      if (rules.supply_policy === 'FIXED') {
        payload.supply_max_cap = null;
      }
      await axios.put(`${API}/portfolios/${portfolioId}/rules`, payload);
      toast.success('Portfolio rules saved');
      await refreshDeployCheck();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save rules');
    } finally {
      setSaving((s) => ({ ...s, rules: false }));
    }
  };

  const saveFunding = async () => {
    setSaving((s) => ({ ...s, funding: true }));
    try {
      const payload = {
        ...funding,
        threshold_amount: Number(funding.threshold_amount) || 0,
        target_amount: Number(funding.target_amount) || 0,
        provider_service_fee_percent: Number(funding.provider_service_fee_percent) || 0,
        provider_service_fee_flat: Number(funding.provider_service_fee_flat) || 0,
      };
      await axios.post(`${API}/portfolios/${portfolioId}/funding-rules`, payload);
      toast.success('Funding rules saved');
      await refreshDeployCheck();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save funding rules');
    } finally {
      setSaving((s) => ({ ...s, funding: false }));
    }
  };

  const saveDistribution = async () => {
    setSaving((s) => ({ ...s, distribution: true }));
    try {
      const payload = {
        ...distribution,
        creator_percent: Number(distribution.creator_percent) || 0,
        buyer_percent: Number(distribution.buyer_percent) || 0,
        provider_percent: Number(distribution.provider_percent) || 0,
        platform_fee_percent: Number(distribution.platform_fee_percent) || 0,
      };
      const sum = payload.creator_percent + payload.buyer_percent
                + payload.provider_percent + payload.platform_fee_percent;
      if (rules.portfolio_type !== 'MARKET_LINKED' && Math.abs(sum - 100) > 0.01) {
        toast.error(`Pool percentages must sum to 100 (current: ${sum})`);
        setSaving((s) => ({ ...s, distribution: false }));
        return;
      }
      await axios.post(`${API}/portfolios/${portfolioId}/distribution-rules`, payload);
      toast.success('Distribution rules saved');
      await refreshDeployCheck();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save distribution rules');
    } finally {
      setSaving((s) => ({ ...s, distribution: false }));
    }
  };

  if (loading) {
    return (
      <div data-testid="rules-editor-loading" style={{ padding: 32 }}>
        Loading portfolio rules…
      </div>
    );
  }

  const requiresProvider =
    rules.portfolio_type === 'PROVIDER_LINKED' || rules.portfolio_type === 'MARKET_LINKED';

  return (
    <div data-testid="portfolio-rules-editor"
         style={{ padding: 32, maxWidth: 1100, margin: '0 auto' }}>
      {/* Heading + back */}
      <button
        data-testid="rules-editor-back"
        onClick={() => navigate(-1)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'transparent', border: 'none', color: theme.colors.primary,
          fontSize: 14, cursor: 'pointer', marginBottom: 14, padding: 0,
        }}
      ><ArrowLeft size={16} /> Back</button>

      <h1 style={{ fontSize: 28, fontWeight: 800, color: theme.colors.text.primary, margin: 0 }}>
        Portfolio Rules Editor
      </h1>
      <p style={{ color: theme.colors.text.secondary, marginTop: 6, fontSize: 14,
                  marginBottom: 24 }}>
        Configure rules, funding, and distribution for{' '}
        <strong data-testid="rules-editor-portfolio-name">
          {portfolio?.name || portfolioId}
        </strong>.
      </p>

      {/* DEPLOYMENT READINESS SUMMARY (Part 3) */}
      <DeploymentReadinessCard
        deployCheck={deployCheck}
        portfolioType={rules.portfolio_type}
        providerCount={providerCount}
      />

      {/* PART 2A — Portfolio Rules */}
      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <div style={sectionTitle}><Settings size={18} /> Portfolio Rules</div>

        <label style={labelStyle}>Portfolio Type</label>
        <div data-testid="portfolio-type-options"
             style={{ display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                      gap: 12, marginBottom: 16 }}>
          {PORTFOLIO_TYPES.map((t) => (
            <button
              key={t.value}
              data-testid={`portfolio-type-${t.value}`}
              onClick={() => setRules({ ...rules,
                portfolio_type: t.value,
                market_participation_enabled: t.value === 'MARKET_LINKED'
                  ? true : rules.market_participation_enabled,
              })}
              style={{
                textAlign: 'left', padding: 14, borderRadius: theme.radius.sm,
                border: `2px solid ${rules.portfolio_type === t.value
                  ? theme.colors.primary : theme.colors.borders}`,
                background: rules.portfolio_type === t.value
                  ? `${theme.colors.primary}10` : theme.colors.bg.page,
                cursor: 'pointer',
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 14,
                            color: theme.colors.text.primary }}>{t.label}</div>
              <div style={{ marginTop: 4, fontSize: 12,
                            color: theme.colors.text.muted }}>{t.description}</div>
            </button>
          ))}
        </div>

        <div style={{ display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                      gap: 14, marginBottom: 14 }}>
          <div>
            <label style={labelStyle}>Sellout Unlock Policy</label>
            <select data-testid="sellout-unlock-policy"
                    value={rules.sellout_unlock_policy} style={inputStyle}
                    onChange={(e) => setRules({ ...rules, sellout_unlock_policy: e.target.value })}>
              {SELLOUT_POLICIES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>NFT Assignment Method</label>
            <select data-testid="nft-assignment-method"
                    value={rules.nft_assignment_method} style={inputStyle}
                    onChange={(e) => setRules({ ...rules, nft_assignment_method: e.target.value })}>
              {NFT_ASSIGNMENT.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Supply Policy</label>
            <select data-testid="supply-policy"
                    value={rules.supply_policy} style={inputStyle}
                    onChange={(e) => setRules({ ...rules, supply_policy: e.target.value })}>
              {SUPPLY_POLICIES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          {rules.supply_policy === 'EXPANDABLE' && (
            <div>
              <label style={labelStyle}>Supply Maximum Cap (required)</label>
              <input data-testid="supply-max-cap" type="number" min="1"
                     value={rules.supply_max_cap ?? ''}
                     style={inputStyle}
                     onChange={(e) => setRules({
                       ...rules, supply_max_cap: e.target.value === ''
                         ? null : Number(e.target.value),
                     })} />
            </div>
          )}
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14,
                        marginTop: 6 }}>
          <input data-testid="market-participation-toggle" type="checkbox"
                 checked={rules.market_participation_enabled}
                 disabled={rules.portfolio_type !== 'MARKET_LINKED'}
                 onChange={(e) => setRules({ ...rules,
                                             market_participation_enabled: e.target.checked })} />
          Market Participation Enabled
          {rules.portfolio_type === 'MARKET_LINKED' && (
            <span style={{ fontSize: 12, color: theme.colors.text.muted,
                           marginLeft: 6 }}>(required for MARKET_LINKED)</span>
          )}
        </label>

        <div style={{ marginTop: 16, padding: 12, borderRadius: theme.radius.sm,
                      background: requiresProvider
                        ? `${theme.colors.status.info}10` : theme.colors.bg.tertiary,
                      border: `1px solid ${requiresProvider
                        ? theme.colors.status.info + '40' : theme.colors.borders}` }}>
          <div style={{ fontSize: 13, fontWeight: 600,
                        color: theme.colors.text.primary,
                        display: 'flex', alignItems: 'center', gap: 6 }}>
            <Users size={14} /> Provider Requirement
          </div>
          <div data-testid="provider-requirement-status"
               style={{ fontSize: 13, color: theme.colors.text.secondary, marginTop: 4 }}>
            {requiresProvider
              ? `Required. Active provider assignments: ${providerCount}.`
              : 'Not required for UTILITY_ONLY portfolios.'}
          </div>
        </div>

        <div style={{ marginTop: 18 }}>
          <button data-testid="save-portfolio-rules" onClick={saveRules}
                  disabled={saving.rules}
                  style={{ padding: '10px 22px', borderRadius: theme.radius.sm,
                           background: theme.colors.primary, color: '#fff',
                           fontWeight: 700, fontSize: 14, border: 'none',
                           cursor: saving.rules ? 'not-allowed' : 'pointer',
                           display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Save size={16} /> {saving.rules ? 'Saving…' : 'Save Portfolio Rules'}
          </button>
        </div>
      </div>

      {/* PART 2B — Funding Rules */}
      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <div style={sectionTitle}><Coins size={18} /> Funding Settings</div>

        <div style={{ display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                      gap: 14 }}>
          <div>
            <label style={labelStyle}>Funding Type</label>
            <select data-testid="funding-type" value={funding.funding_type} style={inputStyle}
                    onChange={(e) => setFunding({ ...funding, funding_type: e.target.value })}>
              <option value="USD">USD</option>
              <option value="CRYPTO">CRYPTO</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Funding Source</label>
            <select data-testid="funding-source" value={funding.funding_source} style={inputStyle}
                    onChange={(e) => setFunding({ ...funding, funding_source: e.target.value })}>
              {FUNDING_SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Threshold Amount</label>
            <input data-testid="funding-threshold-amount" type="number" min="0"
                   value={funding.threshold_amount} style={inputStyle}
                   onChange={(e) => setFunding({ ...funding,
                                                 threshold_amount: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>Target Amount</label>
            <input data-testid="funding-target-amount" type="number" min="0"
                   value={funding.target_amount} style={inputStyle}
                   onChange={(e) => setFunding({ ...funding,
                                                 target_amount: e.target.value })} />
          </div>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14,
                        marginTop: 16 }}>
          <input data-testid="refund-on-failure" type="checkbox"
                 checked={funding.refund_on_failure}
                 onChange={(e) => setFunding({ ...funding,
                                               refund_on_failure: e.target.checked })} />
          Refund on funding failure
        </label>

        <div style={{ marginTop: 18 }}>
          <button data-testid="save-funding-rules" onClick={saveFunding}
                  disabled={saving.funding}
                  style={{ padding: '10px 22px', borderRadius: theme.radius.sm,
                           background: theme.colors.primary, color: '#fff',
                           fontWeight: 700, fontSize: 14, border: 'none',
                           cursor: saving.funding ? 'not-allowed' : 'pointer',
                           display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Save size={16} /> {saving.funding ? 'Saving…' : 'Save Funding Rules'}
          </button>
        </div>
      </div>

      {/* PART 2C — Distribution Rules */}
      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <div style={sectionTitle}><Target size={18} /> Distribution Rules</div>
        <div style={{ display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                      gap: 14, marginBottom: 8 }}>
          <div>
            <label style={labelStyle}>Method</label>
            <select data-testid="distribution-method" value={distribution.method}
                    style={inputStyle}
                    onChange={(e) => setDistribution({ ...distribution,
                                                       method: e.target.value })}>
              {DIST_METHODS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          {['creator_percent', 'buyer_percent', 'provider_percent',
            'platform_fee_percent'].map((k) => (
            <div key={k}>
              <label style={labelStyle}>{k.replace(/_/g, ' ')}</label>
              <input
                data-testid={`dist-${k}`}
                type="number" min="0" max="100"
                value={distribution[k]} style={inputStyle}
                onChange={(e) => setDistribution({ ...distribution,
                                                   [k]: e.target.value })}
              />
            </div>
          ))}
        </div>
        {rules.portfolio_type !== 'MARKET_LINKED' && (
          <div style={{ fontSize: 12, color: theme.colors.text.muted, marginTop: 4 }}>
            Percentages must sum to 100. MARKET_LINKED portfolios ignore pool math
            (uses performance-driven distribution).
          </div>
        )}

        <div style={{ marginTop: 18 }}>
          <button data-testid="save-distribution-rules" onClick={saveDistribution}
                  disabled={saving.distribution}
                  style={{ padding: '10px 22px', borderRadius: theme.radius.sm,
                           background: theme.colors.primary, color: '#fff',
                           fontWeight: 700, fontSize: 14, border: 'none',
                           cursor: saving.distribution ? 'not-allowed' : 'pointer',
                           display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Save size={16} /> {saving.distribution ? 'Saving…' : 'Save Distribution Rules'}
          </button>
        </div>
      </div>
    </div>
  );
};

const DeploymentReadinessCard = ({ deployCheck, portfolioType, providerCount }) => {
  if (!deployCheck) {
    return (
      <div style={{ ...cardStyle, marginBottom: 20 }}
           data-testid="deploy-readiness-card-loading">
        <div style={sectionTitle}><Rocket size={18} /> Deployment Readiness</div>
        <div style={{ color: theme.colors.text.muted, fontSize: 14 }}>
          Checking…
        </div>
      </div>
    );
  }
  const passed = !!deployCheck.can_deploy;
  return (
    <div data-testid="deploy-readiness-card"
         style={{ ...cardStyle, marginBottom: 20,
                  borderLeft: `4px solid ${
                    passed ? theme.colors.status.success : theme.colors.status.error}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={sectionTitle}><Rocket size={18} /> Deployment Readiness</div>
        <span data-testid="deploy-readiness-badge"
              style={{ padding: '6px 14px', borderRadius: 999, fontWeight: 700, fontSize: 13,
                       background: passed
                         ? `${theme.colors.status.success}15`
                         : `${theme.colors.status.error}15`,
                       color: passed
                         ? theme.colors.status.success : theme.colors.status.error,
                       display: 'inline-flex', alignItems: 'center', gap: 8,
                       border: `1px solid ${passed
                         ? theme.colors.status.success : theme.colors.status.error}40` }}>
          {passed ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
          {passed ? 'PASS' : 'BLOCKED'}
        </span>
      </div>
      <div style={{ display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: 12, marginTop: 14, marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: theme.colors.text.muted }}>
          Portfolio Type
          <div style={{ fontSize: 14, color: theme.colors.text.primary, fontWeight: 600 }}>
            {portfolioType || '—'}
          </div>
        </div>
        <div style={{ fontSize: 12, color: theme.colors.text.muted }}>
          Active Providers
          <div style={{ fontSize: 14, color: theme.colors.text.primary, fontWeight: 600 }}>
            {providerCount}
          </div>
        </div>
      </div>

      {!passed && (deployCheck.missing || []).length > 0 && (
        <div data-testid="deploy-blockers" style={{
          marginTop: 6, padding: 12, borderRadius: theme.radius.sm,
          background: `${theme.colors.status.error}08`,
          border: `1px solid ${theme.colors.status.error}30`,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700,
                        color: theme.colors.status.error,
                        marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
            <AlertCircle size={14} /> Blocking reasons:
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, color: theme.colors.text.primary,
                       fontSize: 13 }}>
            {(deployCheck.missing || []).map((m, idx) => (
              <li key={idx} data-testid={`deploy-blocker-${idx}`}>{m}</li>
            ))}
          </ul>
        </div>
      )}
      {(deployCheck.warnings || []).length > 0 && (
        <div data-testid="deploy-warnings" style={{ marginTop: 10, fontSize: 13,
                                                    color: theme.colors.status.warning }}>
          {deployCheck.warnings.length} warning(s): {deployCheck.warnings.join(', ')}
        </div>
      )}
    </div>
  );
};

export default PortfolioRulesEditor;
